import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum SalesOrderLineItemKeys {
  lineItemNumber = 'lineItemNumber', // line item number, not to be entered by user
  itemNumber = 'itemNumber', // user entered item number
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  unitOfMeasure = 'unitOfMeasure', // unit of measure for the item
  itemDiscountRate = 'itemDiscountRate', // discount rate for the item
  itemDiscountPrice = 'itemDiscountPrice', // discount price for the item
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const organizationAddressSchema = yup.object().shape({
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})
export type OrganizationAddress = yup.InferType<typeof organizationAddressSchema>

export const salesOrderFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: organizationAddressSchema.nullable().optional(),
    attn: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  orderNumber: yup.string().nullable().optional(),
  customerNumber: yup.string().nullable().optional(),
  salesperson: yup.string().nullable().optional(),
  issueDate: yup.string().nullable().optional(),
  shipDate: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  shippingMethod: yup.string().nullable().optional(),
  shippingAccountNumber: yup.string().nullable().optional(),
  shippingCost: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  locale: yup.string().nullable().optional(),
  weight: yup.string().nullable().optional(),
  customer: yup.object().shape({
    billingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        billingAddress: organizationAddressSchema.nullable().optional(),
        attn: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    shippingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        shippingAddress: organizationAddressSchema.nullable().optional(),
        attn: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        lineItemNumber: yup.number().nullable().optional(),
        itemNumber: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitOfMeasure: yup.string().nullable().optional(),
        itemDiscountRate: yup.string().nullable().optional(),
        itemDiscountPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  notes: yup.string().nullable().optional(),
  footerText: yup.string().nullable().optional(),
})

export type SalesOrderFileData = yup.InferType<typeof salesOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<SalesOrderFileData>

export const salesOrderFileMappings: RequiredFileData = {
  issuer: {
    companyName: '7020a3b6-4054-421a-8539-56eb0e8d38f9',
    address: {
      addressLine1: '583f3f5f-e1e0-43a6-99be-add540af797a',
      addressLine2: '95b03e4c-fe08-4569-995b-a3fca18687cb',
      postalCode: '739fa907-e389-46d4-8fba-db7302a5f195',
      city: '020ccc4b-8031-4426-9806-136ade539a2c',
      state: '46773ad7-18c0-4107-a170-5e30d1765277',
      country: '37ee752a-bc66-466c-8cb4-cf825e00ee56',
    },
    attn: '6d230553-1b0c-479a-99b7-7fde7791ca67',
    phoneNumber: 'b51ffa75-f854-4061-8cd7-61177c338c6d',
    emailAddress: 'b2974957-f268-4e82-9290-745d5bb69044',
  },
  orderNumber: '8c8132a4-b6bb-433c-a4fe-6adeaed3e93b',
  customerNumber: '77c27ec0-4e2f-43b1-bcd9-a15f8e849763',
  salesperson: 'c7c7d50a-0eae-4671-ad90-6b1c09d3d002',
  issueDate: 'fa46d0f4-209b-4d28-b952-16efe7a6f6d4',
  shipDate: 'd1be1471-42d6-4dfb-ad67-312edd26278c',
  paymentTerms: 'a6e54a8f-f829-452f-925d-51cc411e5f85',
  freightTerms: '8215966f-4598-4c48-9168-794c43000f22',
  shippingMethod: '0bf695c7-8152-40b3-b32d-2fcfbdbd29df',
  shippingAccountNumber: '3d6d5a3f-5a1b-451a-9a23-8a0a2bf1f559',
  shippingCost: '7448094c-bb56-4cb5-a6ca-c04b75ecad09',
  currency: '976d5be2-64d3-41d3-946e-c1404fc2b26c',
  locale: '2496212e-3081-4baa-9f5e-34b538ef98a2',
  weight: '1bcd31bb-834c-444e-aeab-9efa8bd32773',
  customer: {
    billingDetails: {
      companyName: 'b7c20fd2-9c98-468c-9be1-4ba7bf11c123',
      billingAddress: {
        addressLine1: 'fc6ff5bd-afa3-4ffd-85ea-aec0ccb8a227',
        addressLine2: '370b1cda-5fc0-487c-81e1-fedb770770be',
        postalCode: '97908d00-533e-4505-9611-d3a82351b4e9',
        city: 'fd39fd71-39e0-47d8-83ca-07670e27ff04',
        state: 'b7b97d57-c673-4daf-8f97-a555b3d51d1a',
        country: '0bf2b22d-f090-4dd4-b801-393948a03b40',
      },
      attn: '86384be5-cbfa-41ad-9b56-2cef1f6880dc',
      phoneNumber: '12ae96c4-3a16-465f-92f7-fc2a87b62aee',
      emailAddress: 'a9dfc6d0-9aeb-429c-ad6a-53c133e43b21',
    },
    shippingDetails: {
      companyName: 'fcfd62b5-0988-49fc-a291-d73aaae65837',
      shippingAddress: {
        addressLine1: '5dc692fa-8cd7-4320-9e82-4bc42b8ef286',
        addressLine2: '385d65c7-19de-46c3-8723-0599a23b2dcc',
        postalCode: '4deb5627-bff4-44fc-866b-7ae4352d2510',
        city: '55cd2c3c-5911-4620-b955-5e868de129a7',
        state: 'cbf03879-ffcd-4471-94db-964f25591211',
        country: '75b7ae12-e88c-40fa-8b08-7e5645735b84',
      },
      attn: '39e9b873-da78-4dbe-846b-8516f9691431',
      phoneNumber: '5cbfb127-3f04-4c7e-a5ce-b1f8e4e49a7b',
      emailAddress: '3c739ac2-ebd4-4568-b019-a375a9fcdeec',
    },
  },
  items: [
    {
      lineItemNumber: null,
      itemNumber: '0cf37fb9-9eba-4fde-b50a-565179a918b2',
      description: 'c215d52d-eacf-4fd6-a1f9-8fe6c3069728',
      unitPrice: 'b4b72e44-429a-4b10-9d3c-b83889cbe800',
      itemQty: '25104dcf-16e5-4cbb-a362-d2c7eeedf3f1',
      unitOfMeasure: '0e4fcb80-02fc-462a-aa2b-32360392621e',
      itemDiscountRate: 'e3b598ec-7b75-46d3-8829-338275a601e8',
      itemDiscountPrice: 'd9d7fa1f-d7fd-4d23-9e09-229354a19aca',
      lineTotal: null,
    },
  ],
  taxRate: 'c719e455-6c69-423e-90e9-4e6536701157',
  instructions: 'de5a1554-1701-475f-8228-fd1492081f0b',
  notes: '681a2387-9c03-44aa-9858-2d4f1f1b4b8c',
  footerText: 'a55f88e6-3e3d-471a-8873-08fc0fe4847a',
}
